class ProductRewards extends HTMLElement {
  connectedCallback() {
    if (!this.cacheElements()) return;

    this.onVariantUpdate = this.onVariantUpdate.bind(this);
    this.section.addEventListener("variant:update", this.onVariantUpdate);
  }

  cacheElements() {
    this.root = this.querySelector("[data-rewards-root]");
    if (!this.root) return false;

    this.cashbackRowEl = this.root.querySelector("[data-rewards-cashback-row]");
    this.cashbackAmountEl = this.root.querySelector("[data-rewards-cashback-amount]");
    this.cashbackPercentEl = this.root.querySelector("[data-rewards-cashback-percent]");
    this.cashbackPrefixEl = this.root.querySelector("[data-rewards-cashback-prefix]");
    this.ucRowEl = this.root.querySelector("[data-rewards-uc-row]");
    this.ucPriceEl = this.root.querySelector("[data-rewards-uc-price]");
    this.ucCompareEl = this.root.querySelector("[data-rewards-uc-compare]");
    this.ucPercentEl = this.root.querySelector("[data-rewards-uc-percent]");
    this.ucVatWrapEl = this.root.querySelector("[data-rewards-uc-vat]");
    this.ucVatEl = this.ucVatWrapEl?.querySelector(".vat-display");
    this.srpRowEl = this.root.querySelector("[data-rewards-srp-row]");
    this.srpPriceEl = this.root.querySelector("[data-rewards-srp-price]");
    this.srpCompareEl = this.root.querySelector("[data-rewards-srp-compare]");
    this.srpPercentEl = this.root.querySelector("[data-rewards-srp-percent]");
    this.bvRowEl = this.root.querySelector("[data-rewards-bv-row]");
    this.ibvRowEl = this.root.querySelector("[data-rewards-ibv-row]");
    this.bvValueEl = this.root.querySelector("[data-rewards-bv-value]");
    this.ibvValueEl = this.root.querySelector("[data-rewards-ibv-value]");
    this.variantRewards = this.parseVariantRewards();

    this.section = this.closest(".shopify-section, dialog") || document;
    this.isDistributorDisplay = this.datasetBool("isDistributorDisplay");
    this.isMpa = this.datasetBool("isMpa");
    this.isLhp = this.datasetBool("isLhp");
    this.isPpcUfo = this.datasetBool("isPpcUfo");
    this.isUsOrCanada = this.datasetBool("isUsOrCanada");
    this.isIbvOnly = this.datasetBool("isIbvOnly");
    this.allowSrp = this.datasetBool("showSrp");
    this.allowUc = this.datasetBool("showUc");
    this.allowCashback = this.datasetBool("showCashback");
    this.allowBv = this.datasetBool("showBv");
    this.allowIbv = this.datasetBool("showIbv");
    this.lockProductCashback = this.datasetBool("lockProductCashback");
    this.currencyCode = (this.root.dataset.currencyCode || "").toUpperCase();
    // MA-2886: the UC strike-through is PDP-only, matching show_uc_compare in
    // product-rewards.liquid. The same element renders on cards, cart lines and
    // the subscription mini-buybox, which keep the plain UC price.
    this.rewardsContext = this.root.dataset.rewardsContext || "";

    if (this.cashbackPercentEl) {
      const initial = parseFloat(this.cashbackPercentEl.textContent);
      if (Number.isFinite(initial)) this.cashbackPercentEl.textContent = this.formatPercent(initial);
    }

    return true;
  }

  disconnectedCallback() {
    this.section?.removeEventListener("variant:update", this.onVariantUpdate);
  }

  datasetBool(key) {
    return this.root.dataset[key] === "true";
  }

  metafieldValue(metafield) {
    if (metafield == null) return "";
    if (typeof metafield === "object" && "value" in metafield) return metafield.value;
    return metafield;
  }

  numberMetafield(metafield) {
    const value = this.metafieldValue(metafield);
    const number = typeof value === "number" ? value : parseFloat(String(value ?? "").replace(/,/g, ""));
    return Number.isFinite(number) ? number : 0;
  }

  boolMetafield(metafield) {
    const value = this.metafieldValue(metafield);
    return value === true || value === "true";
  }

  resolveCashbackPercent(standard, increased) {
    return increased > standard ? increased : standard;
  }

  formatPercent(percent) {
    const number = Number(percent);
    if (!Number.isFinite(number)) return "0";
    return number.toFixed(2).replace(/\.?0+$/, "");
  }

  formatReward(number) {
    const value = Number(number);
    return Number.isFinite(value) ? value.toFixed(2) : "0.00";
  }

  formatPrice(decimalAmount) {
    const cents = Math.round((Number(decimalAmount) || 0) * 100);
    return this.formatMoney(cents, true);
  }

  // Always the plain money format — mirrors snippets/money.liquid, so the client
  // re-render matches the server. `withCurrency` is accepted but ignored: the
  // store's money_format already carries the currency symbol.
  formatMoney(cents, withCurrency = false) {
    const template = window.Shopify?.moneyFormat ?? window.Shopify?.money_format ?? "${{amount}}";
    const amount = cents / 100;
    const rounded = Math.round(amount);
    const withDotSeparator = rounded.toLocaleString("id-ID");

    return template
      .replace(/\{\{\s*amount\s*\}\}/, amount.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, rounded.toFixed(0))
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.toFixed(2).replace(".", ","))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, rounded.toLocaleString("de-DE"))
      .replace(/\{\{\s*amount_with_apostrophe_separator\s*\}\}/, amount.toFixed(2).replace(".", "'"))
      .replace(/\{\{\s*amount_no_decimals_with_dot_separator\s*\}\}/, withDotSeparator);
  }

  setHidden(element, hidden) {
    if (!element) return;
    element.toggleAttribute("hidden", hidden);
  }

  parseVariantRewards() {
    const script = this.root.querySelector("[data-rewards-variants]");
    if (!script?.textContent) return new Map();

    try {
      return new Map(Object.entries(JSON.parse(script.textContent)));
    } catch {
      return new Map();
    }
  }

  variantIdFromEvent(event) {
    const variantId = event.detail?.resource?.id;
    if (variantId) return String(variantId);

    const html = event.detail?.data?.html;
    if (!html || !this.root?.dataset.productId) return "";

    const selector = `product-rewards [data-rewards-root][data-product-id="${this.root.dataset.productId}"]`;
    const newRoot = html.querySelector(selector);
    return newRoot?.dataset.variantId ? String(newRoot.dataset.variantId) : "";
  }

  rewardDataFromVariant(variant) {
    if (!variant?.id) return null;

    const fields = variant.metafields?.customMA ?? {};
    return {
      id: variant.id,
      price: variant.price,
      standardCashbackPercent: fields.standardCashbackPercent,
      increasedCashbackPercent: fields.increasedCashbackPercent,
      bv: fields.bv,
      ibv: fields.ibv,
      ibvOnly: fields.ibvOnly,
      isOnSale: fields.isOnSale,
      distributorSalePrice: fields.distributorSalePrice,
      distributorOriginalPrice: fields.distributorOriginalPrice,
      // Native field, not a metafield, and in cents here — the embedded reward
      // JSON emits the same key already converted to dollars, so normalise.
      distributorCompareAt: variant.compare_at_price == null ? 0 : Number(variant.compare_at_price) / 100,
      retailSalePrice: fields.retailSalePrice,
      retailOriginalPrice: fields.retailOriginalPrice,
    };
  }

  updateFromHtml(html) {
    if (!html || !this.root?.dataset.productId) return false;

    const selector = `product-rewards [data-rewards-root][data-product-id="${this.root.dataset.productId}"]`;
    const newRoot = html.querySelector(selector);
    const newProductRewards = newRoot?.closest("product-rewards");
    if (!newProductRewards) return false;

    this.innerHTML = newProductRewards.innerHTML;
    const cached = this.cacheElements();
    if (typeof window.renderVatDisplayBlocks === "function") window.renderVatDisplayBlocks();
    return cached;
  }

  updatePriceRow({ row, priceEl, compareEl, percentEl, price, compareAt, allow, showCompare }) {
    const hasPrice = price > 0;
    this.setHidden(row, !allow || !hasPrice);
    if (!priceEl || !hasPrice) return;

    priceEl.textContent = this.formatPrice(price);

    if (!compareEl) return;
    const striking = showCompare && compareAt > price;
    if (striking) {
      compareEl.textContent = this.formatPrice(compareAt);
      compareEl.classList.remove("tw:hidden");
    } else {
      compareEl.textContent = "";
      compareEl.classList.add("tw:hidden");
    }

    // MA-2883: the badge lives and dies with the strike-through beside it — one
    // `striking` test drives both, so they can never disagree after a variant
    // switch. Absent outside the PDP, where the snippet does not emit it.
    if (!percentEl) return;
    // The Liquid pre-selection state may say "Up to 22% off" across a range;
    // from here on a single variant is resolved, so it is a plain "22% off".
    const percent = striking ? Math.round(((compareAt - price) / compareAt) * 100) : 0;
    if (percent > 0) {
      const template = percentEl.dataset.percentTemplate || "[X]% off";
      percentEl.textContent = template.replace("[X]", String(percent));
    } else {
      percentEl.textContent = "";
    }
    // The `hidden` attribute, not a tw:hidden class — see the note in
    // percent-off-badge.liquid on why the class pair does not hide this element.
    this.setHidden(percentEl, percent < 1);
  }

  // Sync the UC GST/VAT line (rendered under the UC price for distributor
  // customers) with the selected variant's UC price. vat-config.js owns the tax
  // rate, country matrix, formatting and tooltip; we just feed it the UC price and
  // collapse the initial min-max range to the chosen variant's single amount. A
  // distinct data-context="product-uc" keeps vat-config's own (native-price)
  // variant handler from touching this block.
  updateUcGst(priceDecimal) {
    if (!this.ucVatWrapEl || !this.ucVatEl) return;
    const cents = Math.round((Number(priceDecimal) || 0) * 100);
    const show = this.allowUc && cents > 0;
    this.ucVatWrapEl.toggleAttribute("hidden", !show);
    if (show) {
      this.ucVatEl.dataset.price = String(cents);
      delete this.ucVatEl.dataset.priceMin;
      delete this.ucVatEl.dataset.priceMax;
    }
    if (typeof window.renderVatDisplayBlocks === "function") window.renderVatDisplayBlocks();
  }

  updateCashback(priceInCents, cashbackPercent, bv) {
    const shouldHideCashback = this.isIbvOnly && bv <= 0;
    const visible = this.allowCashback && cashbackPercent > 0 && !shouldHideCashback;
    this.setHidden(this.cashbackRowEl, !visible);

    // The percent span is absent in `no_percentage` mode (product cards,
    // subscription drawer) — update the amount regardless, and the percent
    // only when its span exists, so variant switches keep the amount fresh.
    if (!this.cashbackAmountEl) return;
    const cashbackAmount = (priceInCents * cashbackPercent) / 100.0;
    this.cashbackAmountEl.textContent = this.formatMoney(cashbackAmount);
    if (this.cashbackPercentEl) this.cashbackPercentEl.textContent = this.formatPercent(cashbackPercent);
  }

  updateBv(bv) {
    this.setHidden(this.bvRowEl, !this.allowBv || bv <= 0);
    if (this.bvValueEl && bv > 0) this.bvValueEl.textContent = this.formatReward(bv);
  }

  updateIbv(ibv) {
    const allow = this.allowIbv && !this.isUsOrCanada && !this.isMpa && (this.isPpcUfo || this.isLhp);
    this.setHidden(this.ibvRowEl, !allow || ibv <= 0);
    if (this.ibvValueEl && ibv > 0) this.ibvValueEl.textContent = this.formatReward(ibv);
  }

  updateCartProperties(priceInCents, cashbackPercent, bv, ibv) {
    const cashbackInput = this.querySelector("[data-rewards-cashback-property]");
    const bvInput = this.querySelector("[data-rewards-bv-property]");
    const ibvInput = this.querySelector("[data-rewards-ibv-property]");

    if (cashbackInput) {
      const cashbackAmount = (priceInCents * cashbackPercent) / 100.0;
      cashbackInput.value = (cashbackAmount / 100).toFixed(2);
    }
    if (bvInput) bvInput.value = this.formatReward(bv);
    if (ibvInput) ibvInput.value = this.formatReward(ibv);
  }

  updateRewards(data) {
    const standard = this.numberMetafield(data.standardCashbackPercent);
    const increased = this.numberMetafield(data.increasedCashbackPercent);
    const cashbackPercent = this.resolveCashbackPercent(standard, increased);
    const bv = this.numberMetafield(data.bv);
    const rawIbv = this.numberMetafield(data.ibv);
    const ibvOnly = this.numberMetafield(data.ibvOnly);
    const ibv = this.isIbvOnly ? ibvOnly : rawIbv;
    const price = parseInt(data.price, 10) || 0;

    const distributorSale = this.numberMetafield(data.distributorSalePrice);
    const distributorOriginal = this.numberMetafield(data.distributorOriginalPrice);
    // MA-2886: the UC strike-through reads the variant's NATIVE compare-at, in
    // dollars. distributorOriginalPrice is not the pre-discount price in practice
    // (0 on some products, below the sale price on others) — see the note on
    // show_uc_compare in product-rewards.liquid.
    const distributorCompareAt = this.numberMetafield(data.distributorCompareAt);
    const retailSale = this.numberMetafield(data.retailSalePrice);
    const retailOriginal = this.numberMetafield(data.retailOriginalPrice);

    if (data.id) this.root.dataset.variantId = String(data.id);
    if (!this.lockProductCashback) this.root.dataset.cashbackPercent = String(cashbackPercent);
    this.root.dataset.bv = String(bv);
    this.root.dataset.ibv = String(ibv);
    this.root.dataset.price = String(price);

    this.updatePriceRow({
      row: this.ucRowEl,
      priceEl: this.ucPriceEl,
      compareEl: this.ucCompareEl,
      percentEl: this.ucPercentEl,
      price: distributorSale || distributorOriginal,
      compareAt: distributorCompareAt,
      allow: this.allowUc,
      // updatePriceRow only strikes when compareAt > price, so a variant whose
      // distributor original matches its sale price shows no strike-through.
      showCompare: this.rewardsContext === "pdp",
    });

    // Keep the UC GST/VAT line in sync with the UC price the customer pays.
    this.updateUcGst(distributorSale || distributorOriginal);

    this.updatePriceRow({
      row: this.srpRowEl,
      priceEl: this.srpPriceEl,
      compareEl: this.srpCompareEl,
      percentEl: this.srpPercentEl,
      price: retailSale || retailOriginal,
      compareAt: retailOriginal,
      allow: this.allowSrp,
      // MA-2886: the SRP row only renders for distributor viewers in the first
      // place (allowSrp mirrors Liquid's show_srp), so gating the strike-through
      // on !isDistributorDisplay meant it could never appear. updatePriceRow's
      // own compareAt > price test is the sale test — the same one the PC view
      // applies — so the isOnSale flag is not consulted here either, matching
      // show_srp_compare in product-rewards.liquid.
      showCompare: true,
    });

    if (!this.lockProductCashback) {
      if (this.cashbackPrefixEl) this.cashbackPrefixEl.textContent = "+";
      this.updateCashback(price, cashbackPercent, bv);
    }
    this.updateBv(bv);
    this.updateIbv(ibv);
    this.updateCartProperties(price, cashbackPercent, bv, ibv);
  }

  onVariantUpdate(event) {
    const eventProductId = event.detail?.data?.productId;
    if (eventProductId && this.root.dataset.productId && eventProductId !== this.root.dataset.productId) return;

    const variantId = this.variantIdFromEvent(event);
    const rewardData = variantId ? this.variantRewards.get(variantId) : null;
    if (rewardData) {
      this.updateRewards({ id: variantId, ...rewardData });
      return;
    }

    const variantData = this.rewardDataFromVariant(event.detail?.resource);
    if (variantData) {
      this.updateRewards(variantData);
      return;
    }

    this.updateFromHtml(event.detail?.data?.html);
  }
}

if (!customElements.get("product-rewards")) {
  customElements.define("product-rewards", ProductRewards);
}
